import { motion, useInView, useScroll, useTransform } from 'framer-motion';
import { useRef, useEffect } from 'react';
import { Link } from 'react-router-dom';
import Header from '../../components/feature/Header';
import Footer from '../../components/feature/Footer';
import WhatsAppButton from '../../components/feature/WhatsAppButton';
import ScrollToTopButton from '../../components/feature/ScrollToTopButton';

const EmpresaPage = () => {
  const heroRef = useRef(null);
  const aboutRef = useRef(null);
  const howWeWorkRef = useRef(null);
  const commitmentRef = useRef(null);

  // Parallax effect for hero
  const { scrollY } = useScroll();
  const heroImageY = useTransform(scrollY, [0, 500], [0, 120]);
  const heroImageScale = useTransform(scrollY, [0, 500], [1, 1.2]);

  const aboutInView = useInView(aboutRef, { once: true, amount: 0.2 });
  const howWeWorkInView = useInView(howWeWorkRef, { once: true, amount: 0.2 });
  const commitmentInView = useInView(commitmentRef, { once: true, amount: 0.2 });

  // Set document metadata
  useEffect(() => {
    document.title = 'Sobre PLADUREX | Empresa de Pladur en Terrassa | +15 Años de Experiencia';
    
    let metaDescription = document.querySelector('meta[name="description"]');
    if (!metaDescription) {
      metaDescription = document.createElement('meta');
      metaDescription.setAttribute('name', 'description');
      document.head.appendChild(metaDescription);
    }
    metaDescription.setAttribute('content', 'PLADUREX, empresa especializada en instalación de pladur en Terrassa con más de 15 años de experiencia. Equipo profesional, materiales de calidad y garantía en todos los trabajos.');

    // Add canonical link
    let canonical = document.querySelector('link[rel="canonical"]');
    if (!canonical) {
      canonical = document.createElement('link');
      canonical.setAttribute('rel', 'canonical');
      document.head.appendChild(canonical);
    }
    canonical.setAttribute('href', `${import.meta.env.VITE_SITE_URL}/empresa`);

    // Add JSON-LD schema
    const script = document.createElement('script');
    script.type = 'application/ld+json';
    script.text = JSON.stringify({
      "@context": "https://schema.org",
      "@type": "AboutPage",
      "name": "Sobre PLADUREX - Empresa de Pladur en Terrassa",
      "description": "Conoce a PLADUREX, empresa especializada en instalación de pladur en Terrassa con más de 15 años de experiencia",
      "url": `${import.meta.env.VITE_SITE_URL}/empresa`,
      "breadcrumb": {
        "@type": "BreadcrumbList",
        "itemListElement": [
          {
            "@type": "ListItem",
            "position": 1,
            "name": "Inicio",
            "item": `${import.meta.env.VITE_SITE_URL}/`
          },
          {
            "@type": "ListItem",
            "position": 2,
            "name": "Empresa",
            "item": `${import.meta.env.VITE_SITE_URL}/empresa`
          }
        ]
      },
      "mainEntity": {
        "@type": "Organization",
        "name": "PLADUREX",
        "description": "Empresa especializada en instalación de pladur con más de 15 años de experiencia en Terrassa y Vallès Occidental",
        "url": `${import.meta.env.VITE_SITE_URL}`,
        "telephone": "+34629501941",
        "address": {
          "@type": "PostalAddress",
          "addressLocality": "Terrassa",
          "addressRegion": "Barcelona",
          "addressCountry": "ES"
        },
        "areaServed": [
          {
            "@type": "City",
            "name": "Terrassa"
          },
          {
            "@type": "AdministrativeArea",
            "name": "Vallès Occidental"
          }
        ],
        "knowsAbout": [
          "Instalación de pladur",
          "Tabiques divisorios",
          "Trasdosados",
          "Falsos techos",
          "Aislamiento acústico",
          "Aislamiento térmico"
        ]
      }
    });
    document.head.appendChild(script);

    return () => {
      if (script.parentNode) {
        script.parentNode.removeChild(script);
      }
    };
  }, []);

  const workSteps = [
    {
      number: '01',
      title: 'Visita y Medición',
      description: 'Nos desplazamos a tu vivienda u oficina en Terrassa para evaluar el proyecto, tomar medidas exactas y entender tus necesidades específicas.'
    },
    {
      number: '02',
      title: 'Propuesta Detallada',
      description: 'Elaboramos un presupuesto claro y detallado, especificando materiales, plazos y acabados. Sin letra pequeña ni sorpresas.'
    },
    {
      number: '03',
      title: 'Ejecución Profesional',
      description: 'Nuestro equipo realiza la instalación con precisión, respetando los plazos acordados y manteniendo el espacio limpio durante toda la obra.'
    },
    {
      number: '04',
      title: 'Remates y Entrega',
      description: 'Realizamos los acabados finales, dejando las superficies listas para pintar o decorar. Revisamos contigo el resultado antes de dar por finalizado.'
    }
  ];

  const commitments = [
    {
      icon: 'ri-medal-line',
      title: 'Calidad Garantizada',
      description: 'Utilizamos materiales de primera calidad y técnicas profesionales para garantizar resultados duraderos y acabados impecables.'
    },
    {
      icon: 'ri-brush-line',
      title: 'Limpieza y Orden',
      description: 'Mantenemos el espacio de trabajo limpio y ordenado. Al finalizar, recogemos todos los residuos y dejamos tu espacio impecable.'
    },
    {
      icon: 'ri-time-line',
      title: 'Puntualidad',
      description: 'Respetamos los plazos acordados y te mantenemos informado del progreso. Tu tiempo es valioso y lo respetamos.'
    }
  ];

  return (
    <div className="bg-deep-green min-h-screen">
      <Header />
      <WhatsAppButton />
      <ScrollToTopButton />

      {/* Hero Section */}
      <section ref={heroRef} className="relative h-[40vh] min-h-[400px] flex items-center justify-center overflow-hidden pt-20">
        <motion.div 
          className="absolute inset-0"
          style={{ y: heroImageY }}
        >
          <motion.img
            src="https://public.readdy.ai/ai/img_res/2f3087378647d6ee7b294bd536f3cca7.jpg"
            alt="Equipo PLADUREX"
            className="w-full h-[120%] object-cover object-top"
            style={{ scale: heroImageScale }}
          />
          <div className="absolute inset-0 bg-deep-green/70"></div>
        </motion.div>

        <div className="relative z-10 text-center px-6">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
          >
            <nav className="text-sm text-gray-300 mb-4">
              <Link to="/" className="hover:text-primary-yellow transition-colors">Inicio</Link>
              <span className="mx-2">&gt;</span>
              <span className="text-primary-yellow">Empresa</span>
            </nav>
            <h1 className="font-display text-5xl lg:text-6xl font-extrabold text-text-light">
              Sobre PLADUREX
            </h1>
          </motion.div>
        </div>
      </section>

      {/* About Section */}
      <section ref={aboutRef} className="py-20">
        <div className="max-w-[1400px] mx-auto px-6">
          <div className="grid lg:grid-cols-[40%_60%] gap-12 items-center">
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              animate={aboutInView ? { opacity: 1, x: 0 } : {}}
              transition={{ duration: 0.6 }}
            >
              <div className="relative rounded-xl overflow-hidden shadow-2xl">
                <img
                  src="https://readdy.ai/api/search-image?query=professional%20drywall%20installer%20working%20with%20tools%20in%20construction%20site&width=500&height=600&seq=empresa-about-001&orientation=portrait"
                  alt="Profesional instalando pladur"
                  className="w-full h-[500px] object-cover object-top"
                />
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 30 }}
              animate={aboutInView ? { opacity: 1, x: 0 } : {}}
              transition={{ duration: 0.6, delay: 0.2 }}
            >
              <h2 className="font-display text-4xl lg:text-5xl font-bold text-text-light mb-6">
                Especialistas en instalación de pladur en Terrassa
              </h2>
              <div className="space-y-4 text-gray-300 text-[17px] leading-relaxed">
                <p>
                  En PLADUREX somos especialistas en la <strong className="text-text-light">instalación de pladur en Terrassa</strong> y toda la comarca del Vallès. Ofrecemos soluciones profesionales para viviendas, oficinas y locales comerciales.
                </p>
                <p>
                  Nuestro equipo cuenta con amplia experiencia en todo tipo de proyectos: desde la instalación de un simple <Link to="/servicios" className="text-primary-yellow hover:text-accent-lime transition-colors">tabique divisorio</Link> hasta reformas integrales con <Link to="/servicios" className="text-primary-yellow hover:text-accent-lime transition-colors">trasdosados, falsos techos y sistemas de aislamiento</Link> acústico y térmico.
                </p>
                <p>
                  Trabajamos con los mejores materiales del mercado y aplicamos técnicas profesionales que garantizan acabados impecables y duraderos. Cada proyecto es único y lo tratamos con la atención que merece.
                </p>
              </div>

              <div className="mt-8 space-y-3">
                <div className="flex items-center gap-3">
                  <i className="ri-checkbox-circle-fill text-accent-lime text-xl"></i>
                  <span className="text-gray-300">Materiales de primera calidad</span>
                </div>
                <div className="flex items-center gap-3">
                  <i className="ri-checkbox-circle-fill text-accent-lime text-xl"></i>
                  <span className="text-gray-300">Equipo profesional y experimentado</span>
                </div>
                <div className="flex items-center gap-3">
                  <i className="ri-checkbox-circle-fill text-accent-lime text-xl"></i>
                  <span className="text-gray-300">Trabajo en viviendas y oficinas ocupadas</span>
                </div>
                <div className="flex items-center gap-3">
                  <i className="ri-checkbox-circle-fill text-accent-lime text-xl"></i>
                  <span className="text-gray-300">Factura y garantía en todos los trabajos</span>
                </div>
              </div>
              
              {/* Link to services */}
              <div className="mt-8">
                <Link
                  to="/servicios"
                  className="inline-flex items-center gap-2 px-6 py-3 bg-primary-yellow text-deep-green font-bold rounded-lg hover:shadow-[0_0_25px_rgba(255,212,0,0.5)] hover:-translate-y-1 transition-all duration-300 whitespace-nowrap"
                >
                  Ver todos nuestros servicios
                  <i className="ri-arrow-right-line"></i>
                </Link>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* How We Work Section */}
      <section ref={howWeWorkRef} className="py-20 bg-card-green/30">
        <div className="max-w-[1200px] mx-auto px-6">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={howWeWorkInView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.6 }}
            className="text-center mb-16"
          >
            <h2 className="font-display text-4xl lg:text-5xl font-bold text-text-light mb-4">
              Cómo trabajamos
            </h2>
            <div className="w-16 h-1 bg-primary-yellow mx-auto mb-4"></div>
            <p className="text-gray-300 text-lg max-w-[700px] mx-auto">
              Proceso transparente y profesional desde el primer contacto hasta la entrega final
            </p>
          </motion.div>

          <div className="space-y-8">
            {workSteps.map((step, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, x: index % 2 === 0 ? -30 : 30 }}
                animate={howWeWorkInView ? { opacity: 1, x: 0 } : {}}
                transition={{ duration: 0.6, delay: index * 0.15 }}
                className={`flex flex-col md:flex-row items-center gap-8 ${
                  index % 2 === 0 ? '' : 'md:flex-row-reverse'
                }`}
              >
                <div className="flex-shrink-0">
                  <div className="w-24 h-24 rounded-full bg-gradient-to-br from-primary-yellow to-accent-lime flex items-center justify-center">
                    <span className="text-4xl font-bold text-deep-green">{step.number}</span>
                  </div>
                </div>
                <div className={`flex-1 bg-card-green p-8 rounded-xl ${index % 2 === 0 ? '' : 'text-right'}`}>
                  <h3 className="text-2xl font-bold text-text-light mb-3">{step.title}</h3>
                  <p className="text-gray-300 leading-relaxed">{step.description}</p>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Commitment Section */}
      <section ref={commitmentRef} className="py-20">
        <div className="max-w-[1400px] mx-auto px-6">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={commitmentInView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.6 }}
            className="text-center mb-16"
          >
            <h2 className="font-display text-4xl lg:text-5xl font-bold text-text-light mb-4">
              Nuestro compromiso
            </h2>
            <div className="w-16 h-1 bg-primary-yellow mx-auto"></div>
          </motion.div>

          <div className="grid md:grid-cols-3 gap-8">
            {commitments.map((item, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 30 }}
                animate={commitmentInView ? { opacity: 1, y: 0 } : {}}
                transition={{ duration: 0.6, delay: index * 0.15 }}
                className="bg-card-green p-8 rounded-xl text-center hover:scale-105 transition-transform duration-300"
              >
                <div className="w-20 h-20 rounded-full bg-gradient-to-br from-primary-yellow to-accent-lime flex items-center justify-center mx-auto mb-6">
                  <i className={`${item.icon} text-deep-green text-3xl`}></i>
                </div>
                <h3 className="text-2xl font-bold text-text-light mb-4">{item.title}</h3>
                <p className="text-gray-300 leading-relaxed">{item.description}</p>
              </motion.div>
            ))}
          </div>
          
          {/* Links to FAQ and Contact */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={commitmentInView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.6, delay: 0.6 }}
            className="text-center mt-12 space-y-4"
          >
            <p className="text-gray-300">
              ¿Tienes dudas sobre nuestros servicios? Consulta nuestras{' '}
              <Link to="/faq" className="text-primary-yellow hover:text-accent-lime transition-colors font-semibold">
                preguntas frecuentes sobre pladur
              </Link>
            </p>
          </motion.div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-gradient-to-b from-card-green to-black">
        <div className="max-w-[900px] mx-auto px-6 text-center">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
          >
            <h2 className="font-display text-4xl lg:text-5xl font-bold text-text-light mb-4">
              ¿Hablamos de tu proyecto?
            </h2>
            <p className="text-xl text-gray-300 mb-8">
              Contacta con nosotros y te asesoramos sin compromiso
            </p>
            <div className="flex flex-wrap justify-center gap-4">
              <a
                href="tel:+34629501941"
                className="px-10 py-5 bg-primary-yellow text-deep-green font-bold text-lg rounded-lg hover:shadow-[0_0_40px_rgba(255,212,0,0.6)] hover:-translate-y-1 transition-all duration-300 flex items-center gap-3 whitespace-nowrap"
              >
                <i className="ri-phone-line text-xl"></i>
                629 501 941
              </a>
              <a
                href="https://wa.me/34629501941"
                target="_blank"
                rel="noopener noreferrer"
                className="px-10 py-5 border-2 border-primary-yellow text-primary-yellow font-bold text-lg rounded-lg hover:bg-primary-yellow hover:text-deep-green transition-all duration-300 flex items-center gap-3 whitespace-nowrap"
              >
                <i className="ri-whatsapp-line text-xl"></i>
                WhatsApp
              </a>
            </div>
          </motion.div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default EmpresaPage;
